/* eslint-disable max-classes-per-file */
import { FilledInput, FormControl, FormHelperText, Input, InputLabel, OutlinedInput, TextField } from '@mui/material';
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import { InputProps } from '@mui/material/Input';
import { InputLabelProps } from '@mui/material/InputLabel';
import { TextFieldProps } from '@mui/material/TextField';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { ValidatorComponent } from 'react-form-validator-core';
import { compose } from 'recompose';

import { WithStyles, withStyles } from '@/hocs/with-styles';

export interface InputValidatorProps {
  requiredError?: string;
  // Array of error messages. Order of messages should be the same as validators prop.
  errorMessages?: string[];
  // Array of validators. See list of default validators above.
  validators?: string[];
  // Classname of the container class.
  containerClass?: string;
  // Helper text, shown below the input element.
  helperText?: string | React.ReactNode;

  inputLabelProps?: InputLabelProps;
  // The label of the element.
  label?: string | React.ReactNode;
  // It triggers after each validation. It will return true or false
  validatorListener?: unknown;
  // Name of input
  name: string;
  // Allow to use required validator in any validation trigger, not only form submit.
  withRequiredValidator?: boolean;

  variant?: 'standard' | 'filled' | 'outlined';

  labelWidth?: number;
}
export interface SelectValidatorProps {
  getOptionSelected?: Function;
  optionsArray: Array<{ [key: string]: string }>;
  onChangeProp: (value: { [key: string]: string }) => void;
  filterOptions?: (options: any[], input: { inputValue: string }) => any[];
  selectedValue: { [key: string]: string };
}

export interface MultiSelectValidatorProps {
  optionsArray: Array<{ [key: string]: string }>;
  onChangeProp: (
    value: {
      [key: string]: string;
    }[],
  ) => void;
  selectedValue: (
    | string
    | {
        [key: string]: string;
      }
  )[];
  multiple?: boolean;
}

interface State {
  isValid: boolean;
}

const inputVariantComponent = {
  standard: Input,
  filled: FilledInput,
  outlined: OutlinedInput,
};

const styles = () => ({
  star: {
    '&:after': {
      content: "' *'",
    },
  },
});

// tslint:disable:max-classes-per-file
class InputValidator extends ValidatorComponent<InputValidatorProps & InputProps, State> {
  state: State = {
    isValid: true,
  };

  render() {
    const {
      errorMessages,
      validators = [],
      containerClass,
      requiredError,
      helperText,
      inputLabelProps,
      label,
      validatorListener,
      onKeyPress,
      variant,
      labelWidth: propLabelWidth,
      classes,
      ...rest
    } = this.props as InputValidatorProps & InputProps & WithStyles<typeof styles>;
    let labelWidth = propLabelWidth;
    const { isValid } = this.state;
    // Unless overridden, we disable enter key in the input.
    const defaultKeyPress = (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
      }
    };
    let useOnKeyPress = defaultKeyPress;
    if (onKeyPress) {
      useOnKeyPress = onKeyPress;
    }

    const InputVariantComponent = inputVariantComponent.standard;
    const FilledInputVariantComponent = inputVariantComponent.filled;
    const OutlinedInputComponent = inputVariantComponent.outlined;

    if (variant === 'outlined' && !labelWidth) {
      labelWidth = 60;
    }

    rest.required = rest?.required ?? validators?.includes('required');
    const starAtEnd = validators?.includes('required') ? classes.star : '';
    return (
      <FormControl variant="filled" fullWidth className={containerClass}>
        <InputLabel {...inputLabelProps} className={clsx(inputLabelProps?.className, starAtEnd)}>
          {label}
        </InputLabel>
        {variant === undefined || variant === 'filled' ? (
          <FilledInputVariantComponent {...rest} error={!isValid} onKeyPress={useOnKeyPress} />
        ) : variant === 'outlined' ? (
          <OutlinedInputComponent {...rest} error={!isValid} onKeyPress={useOnKeyPress} />
        ) : (
          <InputVariantComponent {...rest} error={!isValid} onKeyPress={useOnKeyPress} />
        )}
        <FormHelperText id="weight-helper-text" error={!isValid}>
          {(!isValid && this.getErrorMessage()) || helperText}
        </FormHelperText>
      </FormControl>
    );
  }
}
const enhance = compose<unknown, InputValidatorProps & InputProps>(withStyles(styles));
export default enhance(InputValidator as React.ComponentType<unknown>) as unknown as typeof InputValidator;

export class SelectValidator extends ValidatorComponent<InputValidatorProps & InputProps, State> {
  render() {
    /* eslint-disable no-unused-vars */
    const {
      error,
      errorMessages,
      validators,
      requiredError,
      helperText,
      validatorListener,
      withRequiredValidator,
      containerClass,
      ...rest
    } = this.props;
    const { isValid } = this.state;
    return (
      <FormControl variant="filled" fullWidth className={containerClass}>
        <TextField
          {...(rest as TextFieldProps)}
          select
          error={!isValid || error}
          helperText={(!isValid && this.getErrorMessage()) || helperText}
        />
      </FormControl>
    );
  }
}

/**
 * Wrap form components in a validator component, that will display the error text, and set a
 * passed in `validClass`/`invalidClass`.
 */
export class WrapValidator extends ValidatorComponent<
  InputValidatorProps & InputProps & { invalidClass?: string; validClass?: string },
  State
> {
  render() {
    /* eslint-disable no-unused-vars */
    const {
      error,
      errorMessages,
      validators,
      requiredError,
      helperText,
      validatorListener,
      withRequiredValidator,
      containerClass,
      children,
      invalidClass,
      validClass,
      ...rest
    } = this.props;
    const { isValid } = this.state;
    const formContainerClass = `${containerClass} ${
      isValid ? (validClass ? validClass : '') : invalidClass ? invalidClass : ''
    }`;
    return (
      <FormControl variant="filled" fullWidth className={formContainerClass}>
        {children}
        <FormHelperText id="weight-helper-text" error={!isValid}>
          {(!isValid && this.getErrorMessage()) || helperText}
        </FormHelperText>
      </FormControl>
    );
  }
}

type ExtendedProperties = InputValidatorProps & SelectValidatorProps & InputProps;

export const SelectWithSearchValidator: React.FC<ExtendedProperties> = ({
  error,
  errorMessages,
  helperText,
  containerClass,
  optionsArray,
  onChangeProp,
  getOptionSelected,
  filterOptions,
  selectedValue,
  ...rest
}) => {
  const sortedOptions = useMemo(
    () =>
      [...optionsArray].sort((a, b) => {
        const nameA = a.name.toLowerCase(),
          nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          //sort string ascending
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }),
    [optionsArray],
  );

  const handleChange = (event: React.ChangeEvent<object>, value: null | { [key: string]: string }) => {
    if (!value) {
      return;
    }

    onChangeProp(value);
  };

  return (
    <FormControl variant="filled" fullWidth className={containerClass}>
      <Autocomplete
        id="search-box"
        options={sortedOptions}
        filterOptions={filterOptions}
        getOptionLabel={(option: any) => option.name || ''}
        onChange={handleChange as any}
        isOptionEqualToValue={getOptionSelected as any}
        defaultValue={selectedValue}
        renderInput={(params) => (
          <TextField {...params} {...(rest as TextFieldProps)} error={error} helperText={errorMessages || helperText} />
        )}
      />
    </FormControl>
  );
};

type ExtendedMultiSelectProperties = InputValidatorProps & MultiSelectValidatorProps & InputProps;

export const MultiSelectWithSearchValidator: React.FC<ExtendedMultiSelectProperties> = ({
  error,
  errorMessages,
  validators,
  requiredError,
  helperText,
  validatorListener,
  withRequiredValidator,
  containerClass,
  optionsArray,
  onChangeProp,
  selectedValue,
  multiple = false,
  ...rest
}) => {
  const isValid = useState(Boolean);

  const sortedOptions = useMemo(
    () =>
      [...optionsArray].sort((a, b) => {
        const nameA = a.name.toLowerCase(),
          nameB = b.name.toLowerCase();
        if (nameA < nameB) {
          //sort string ascending
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }),
    [optionsArray],
  );

  const handleChange = (
    event: React.ChangeEvent<{}>,
    value: (
      | string
      | {
          [key: string]: string;
        }
    )[],
    _reason: AutocompleteChangeReason,
  ) => {
    onChangeProp(
      value as {
        [key: string]: string;
      }[],
    );
  };
  return (
    <FormControl variant="filled" fullWidth className={containerClass}>
      <Autocomplete
        multiple
        id="multi-select"
        options={sortedOptions}
        getOptionLabel={(option: { [key: string]: string }) => option.name}
        defaultValue={selectedValue as any}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            variant="filled"
            {...params}
            {...(rest as TextFieldProps)}
            error={!isValid || error}
            helperText={(!isValid && errorMessages) || helperText}
          />
        )}
      />
    </FormControl>
  );
};
